/**
 * Notification Service
 * Centralized service for displaying toast notifications using react-toastify
 */

import { toast, ToastOptions } from 'react-toastify';

interface NotificationOptions extends Partial<ToastOptions> {
    autoClose?: number;
    position?: ToastOptions['position'];
}

class NotificationService {
    private defaultOptions: ToastOptions = {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined
    };

    /**
     * Show success notification
     */
    success(message: string, options?: NotificationOptions): void {
        toast.success(message, {
            ...this.defaultOptions,
            ...options
        });
    }

    /**
     * Show error notification
     */
    error(message: string, options?: NotificationOptions): void {
        toast.error(message, {
            ...this.defaultOptions,
            autoClose: 5000, // errors stay longer
            ...options
        });
    }

    /**
     * Show info notification
     */
    info(message: string, options?: NotificationOptions): void {
        toast.info(message, {
            ...this.defaultOptions,
            ...options
        });
    }

    /**
     * Show warning notification
     */
    warning(message: string, options?: NotificationOptions): void {
        toast.warning(message, {
            ...this.defaultOptions,
            autoClose: 4000,
            ...options
        });
    }

    /**
     * Show a promise-based notification
     * useful for async operations with loading, success, and error states
     */
    promise<T>(
        promise: Promise<T>,
        messages: {
            pending: string;
            success: string;
            error: string;
        },
        options?: NotificationOptions
    ): Promise<T> {
        return toast.promise(
            promise,
            {
                pending: messages.pending,
                success: messages.success,
                error: messages.error
            },
            {
                ...this.defaultOptions,
                ...options
            }
        ) as Promise<T>;
    }

    /**
     * Dismiss a specific toast by ID
     */
    dismiss(toastId?: string | number): void {
        toast.dismiss(toastId);
    }

    /**
     * Dismiss all toasts
     */
    dismissAll(): void {
        toast.dismiss();
    }

    /**
     * Check if a toast is active
     */
    isActive(toastId: string | number): boolean {
        return toast.isActive(toastId);
    }
}

// export singleton instance
export const notificationService = new NotificationService();

// export class for testing or custom instances
export default NotificationService;

